/** Liquid Glass — единая геометрия плиток конфигуратора (2 колонки, хвост на всю ширину). */

import type { CSSProperties } from 'react';

export const CFG_CHIP_GAP = 'gap-3.5';
export const CFG_CHIP_ROW = `flex flex-wrap ${CFG_CHIP_GAP}`;
/** Базовая сетка 2×N (без правила хвоста — только для внутренних случаев). */
export const CFG_CHIP_GRID = `grid grid-cols-2 ${CFG_CHIP_GAP}`;
/**
 * Стандартная сетка выбора: 2 колонки; если в ряду одна плитка — на всю ширину.
 * Matter, один протокол, нечётное число цветов/тегов.
 */
export const CFG_CHIP_GRID_WIDE_LAST =
  `grid grid-cols-2 ${CFG_CHIP_GAP} [&>*:last-child:nth-child(odd)]:col-span-2 [&>*:only-child]:col-span-2`;

/** Алиас для навигаторов витрины / PIM / рулонных. */
export const CFG_NAV_GRID_WIDE_LAST = CFG_CHIP_GRID_WIDE_LAST;

/** Открывание: влево/вправо сверху, «к центру» снизу на всю ширину. */
export const CFG_CHIP_OPENING_GRID = `grid grid-cols-2 ${CFG_CHIP_GAP}`;
export const CFG_CHIP_OPENING_CENTER = 'col-span-2';

export const CFG_CHIP_TRANSITION =
  'transition-[grid-column,box-shadow,background-color,border-color,color,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]';

/** @deprecated Prefer cfgChipGridSpan(index, total, isActive) in grids. */
export function cfgChipSpanClass(isActive: boolean): string {
  return isActive ? `col-span-2 w-full ${CFG_CHIP_TRANSITION}` : `w-full ${CFG_CHIP_TRANSITION}`;
}

export function cfgChipSpanStyle(isActive: boolean): CSSProperties | undefined {
  return isActive ? { gridColumn: '1 / -1' } : undefined;
}

export type CfgChipGridSpanOptions = {
  /** Выбранная плитка растягивается на всю строку (цвет, wireless-протокол). */
  activeFullWidth?: boolean;
};

/**
 * Явный span для сетки 2×N: хвост в неполном ряду (Matter) и/или активный выбор на всю ширину.
 * gridColumn inline — fallback для Telegram WebView, где col-span иногда не срабатывает.
 */
export function cfgChipGridSpan(
  index: number,
  total: number,
  isActive: boolean,
  options?: CfgChipGridSpanOptions,
): { className: string; style?: CSSProperties } {
  const activeFullWidth = options?.activeFullWidth !== false;
  const loneInLastRow = total % 2 === 1 && index === total - 1;
  const spanFull = loneInLastRow || (activeFullWidth && isActive);
  return {
    className: spanFull ? `col-span-2 w-full ${CFG_CHIP_TRANSITION}` : `w-full ${CFG_CHIP_TRANSITION}`,
    style: spanFull ? { gridColumn: '1 / -1' } : undefined,
  };
}

export function mergeChipGridStyle(
  base: CSSProperties | undefined,
  span: { style?: CSSProperties },
): CSSProperties | undefined {
  if (!base && !span.style) return undefined;
  return { ...base, ...span.style };
}

/** Активный вариант первым — выбранная плитка сверху во всю ширину. */
export function cfgChipSortActiveFirst<T>(items: T[], isActive: (item: T) => boolean): T[] {
  return [...items].sort((a, b) => {
    const sa = isActive(a);
    const sb = isActive(b);
    if (sa && !sb) return -1;
    if (!sa && sb) return 1;
    return 0;
  });
}

export const CFG_CHIP_GRID_3 = 'grid grid-cols-3 gap-2.5 sm:gap-3';
export const CFG_CHIP_GRID_2 = `grid grid-cols-2 gap-2.5 sm:gap-3.5`;

export const CFG_CHIP_NAV_COMPACT =
  'press flex flex-col items-center justify-center gap-0.5 min-h-[56px] px-2.5 py-3 rounded-2xl text-center transition-all disabled:opacity-40';

export const CFG_CHIP_TIER =
  'press flex-1 min-w-[calc(50%-0.4375rem)] flex items-center justify-center min-h-[64px] px-5 py-4 rounded-2xl text-base font-bold tracking-tight transition-all disabled:opacity-35';

export const CFG_CHIP_PRIMARY =
  'press flex-1 flex items-center justify-center gap-2 min-h-[64px] px-5 py-4 rounded-2xl text-base font-bold tracking-tight transition-all disabled:opacity-35';

export const CFG_CHIP_POWER_SOLO =
  'press w-full flex flex-col items-center justify-center gap-2 min-h-[92px] px-6 py-5 rounded-2xl text-lg font-bold tracking-tight transition-all disabled:opacity-35';

export const CFG_CHIP_TILE =
  'press flex w-full flex-col items-center justify-center gap-1.5 min-h-[64px] px-4 py-3.5 rounded-2xl text-[15px] font-bold leading-snug text-center transition-all disabled:opacity-40';

export const CFG_CHIP_COLOR =
  'press flex w-full items-center justify-center min-h-[64px] px-5 py-4 rounded-2xl text-[15px] font-bold tracking-tight transition-all disabled:opacity-35';

export const CFG_CHIP_TAG =
  'press flex w-full items-center justify-center min-h-[58px] px-4 py-3.5 rounded-2xl text-sm font-bold transition-all disabled:opacity-40';

export const CFG_CHIP_SIZE =
  'press flex flex-col items-center justify-center min-h-[64px] py-4 px-2 rounded-2xl transition-all';

export const CFG_SUMMARY_CHIP = 'text-[11px] px-2.5 py-1 rounded-full font-medium';
